import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Copy, KeyRound, TriangleAlert } from 'lucide-react';
import { api } from '@/lib/api';
import { useApi, rowsOf } from '@/Hooks/useApi';
import { usePermission } from '@/Hooks/usePermission';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Field, Select } from '@/components/ui/field';
import { Icon } from '@/components/ui/icon';
import { Input } from '@/components/ui/input';
import { LoadingState, ErrorState, EmptyState } from '@/components/ui/states';
import { apiMessage, errorCode } from '../helpers';
import type { Branch } from '../types';
import ConfirmDialog from './ConfirmDialog';

const BOOKING_WIDGET_NOT_CONFIGURED = 'BOOKING_WIDGET_NOT_CONFIGURED';
const DAYS = [0, 1, 2, 3, 4, 5, 6] as const;

/** One weekday's public booking window — mirrors `OpeningWindowDto`. */
interface OpeningWindow {
  day: number;
  from: string;
  to: string;
}

/** `withEmbedToken()`'s shape on the wire — the row plus its live embed token. */
interface BookingWidgetView {
  id: string;
  branchId: string;
  publicId: string;
  keyVersion: number;
  isEnabled: boolean;
  slotMinutes: number;
  leadTimeMinutes: number;
  horizonDays: number;
  minPartySize: number;
  maxPartySize: number;
  openingHours: OpeningWindow[] | null;
  autoConfirm: boolean;
  waitlistEnabled: boolean;
  createdAt: string;
  updatedAt: string;
  /** Public by design — this IS the credential a venue pastes into its site. Never logged, never sent anywhere but this screen and the venue's own paste target. */
  embedToken: string;
}

interface WidgetForm {
  isEnabled: boolean;
  slotMinutes: string;
  leadTimeMinutes: string;
  horizonDays: string;
  minPartySize: string;
  maxPartySize: string;
  autoConfirm: boolean;
  waitlistEnabled: boolean;
  openingHours: OpeningWindow[];
}

function formOf(widget: BookingWidgetView | null): WidgetForm {
  return {
    isEnabled: widget?.isEnabled ?? false,
    slotMinutes: String(widget?.slotMinutes ?? 90),
    leadTimeMinutes: String(widget?.leadTimeMinutes ?? 60),
    horizonDays: String(widget?.horizonDays ?? 30),
    minPartySize: String(widget?.minPartySize ?? 1),
    maxPartySize: String(widget?.maxPartySize ?? 12),
    autoConfirm: widget?.autoConfirm ?? true,
    waitlistEnabled: widget?.waitlistEnabled ?? true,
    openingHours: widget?.openingHours ?? [],
  };
}

function windowOf(hours: OpeningWindow[], day: number): OpeningWindow | null {
  return hours.find((w) => w.day === day) ?? null;
}

/**
 * §13 / ADR-0018 — a branch's public booking page: its configuration, and the
 * embed token that is the only thing allowed to name a tenant on an
 * unauthenticated request (`widget-token.ts`).
 *
 * Per-branch, like `WifiPanel`: `BookingWidget.branchId` is `@unique`, one
 * widget per branch, so the branch picker IS the record picker rather than a
 * filter over a list.
 *
 * The embed token is treated as a credential throughout, not as a display
 * value: masked by default (an operator reveals it on purpose), forced
 * `dir="ltr"` so it cannot mirror into nonsense in the Arabic UI, and never
 * interpolated into a toast or any state this screen keeps beyond what is on
 * screen right now. Unlike an `ApiClient` secret it is not "shown once" — the
 * backend hands it back on every `GET`/`PUT`, because it is not a bearer
 * credential for THIS API, it is the value a venue pastes into its own site
 * and has to be able to retrieve again. What IS one-shot is the `keyVersion`
 * a rotation bumps: the moment that commits, every embed token issued before
 * it — including one already pasted into a live page — stops verifying.
 */
export default function BookingWidgetPanel() {
  const { t } = useTranslation();
  const { can } = usePermission();
  const canConfigure = can('booking_widget.configure');
  const canRotate = can('booking_widget.rotate_key');

  const branchesQuery = useApi<unknown>('/branches', { limit: 200 });
  const branches = rowsOf<Branch>(branchesQuery.data).rows;

  const [branchId, setBranchId] = useState('');
  const currentBranchId = branchId || branches[0]?.id || '';
  const branchName = branches.find((b) => b.id === currentBranchId)?.name ?? '';

  const widgetQuery = useApi<BookingWidgetView>(
    currentBranchId ? `/booking-widgets/${currentBranchId}` : null,
  );
  const notConfigured =
    Boolean(widgetQuery.error) && errorCode(widgetQuery.rawError) === BOOKING_WIDGET_NOT_CONFIGURED;

  const widget = widgetQuery.data;
  const [form, setForm] = useState<WidgetForm>(formOf(null));
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [revealToken, setRevealToken] = useState(false);
  const [rotating, setRotating] = useState(false);

  useEffect(() => {
    setForm(formOf(widget ?? null));
    setRevealToken(false);
  }, [widget, currentBranchId]);

  const partyRangeInvalid = Number(form.minPartySize) > Number(form.maxPartySize);

  const setWindow = (day: number, patch: Partial<OpeningWindow> | null) => {
    setForm((prev) => {
      const existing = windowOf(prev.openingHours, day);
      if (patch === null) {
        return { ...prev, openingHours: prev.openingHours.filter((w) => w.day !== day) };
      }
      const next: OpeningWindow = { day, from: existing?.from ?? '09:00', to: existing?.to ?? '22:00', ...patch };
      const others = prev.openingHours.filter((w) => w.day !== day);
      return { ...prev, openingHours: [...others, next].sort((a, b) => a.day - b.day) };
    });
  };

  const save = async () => {
    if (partyRangeInvalid) {
      setSaveError(t('settings.booking.partyRangeInvalid'));
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      await api.put(`/booking-widgets/${currentBranchId}`, {
        isEnabled: form.isEnabled,
        slotMinutes: Number(form.slotMinutes),
        leadTimeMinutes: Number(form.leadTimeMinutes),
        horizonDays: Number(form.horizonDays),
        minPartySize: Number(form.minPartySize),
        maxPartySize: Number(form.maxPartySize),
        autoConfirm: form.autoConfirm,
        waitlistEnabled: form.waitlistEnabled,
        openingHours: form.openingHours,
      });
      toast.success(t('settings.booking.saved'));
      widgetQuery.reload();
    } catch (error) {
      setSaveError(apiMessage(error, t('settings.errors.actionFailed')));
    } finally {
      setSaving(false);
    }
  };

  const copyToken = async (token: string) => {
    try {
      await navigator.clipboard.writeText(token);
      toast.success(t('integrations.secret.copied'));
    } catch {
      toast.error(t('integrations.secret.copyFailed'));
    }
  };

  const rotate = async () => {
    try {
      await api.post(`/booking-widgets/${currentBranchId}/rotate-key`);
      toast.success(t('settings.booking.rotated'));
      widgetQuery.reload();
      setRevealToken(true);
    } catch (error) {
      toast.error(apiMessage(error, t('settings.errors.actionFailed')));
    } finally {
      setRotating(false);
    }
  };

  if (branchesQuery.loading) return <LoadingState label={t('common.loading')} />;
  if (branchesQuery.error) {
    return <ErrorState message={branchesQuery.error} onRetry={branchesQuery.reload} />;
  }
  if (!branches.length) {
    return <EmptyState title={t('settings.branches.empty')} hint={t('settings.booking.needBranch')} />;
  }

  return (
    <div className="space-y-5">
      <div className="max-w-xs">
        <Field label={t('settings.booking.branchLabel')}>
          <Select
            value={currentBranchId}
            onChange={(e) => setBranchId(e.target.value)}
            aria-label={t('settings.booking.branchLabel')}
          >
            {branches.map((branch) => (
              <option key={branch.id} value={branch.id}>
                {branch.name}
              </option>
            ))}
          </Select>
        </Field>
      </div>

      {widgetQuery.loading ? (
        <LoadingState label={t('common.loading')} />
      ) : widgetQuery.error && !notConfigured ? (
        // A real failure — never rendered the same as "not configured yet"
        // below, or an operator would be prompted to configure a widget that
        // may already exist and simply failed to load, minting a second one
        // is not possible (branchId is unique) but the confusion costs a
        // support ticket either way.
        <ErrorState message={widgetQuery.error} onRetry={widgetQuery.reload} />
      ) : notConfigured ? (
        <EmptyState
          title={t('settings.booking.emptyTitle')}
          hint={t('settings.booking.emptyHint')}
          action={
            canConfigure && (
              <Button size="sm" onClick={() => void save()} disabled={saving}>
                {t('settings.booking.createWidget')}
              </Button>
            )
          }
        />
      ) : (
        widget && (
          <>
            <section className="space-y-4 rounded-xl border p-5">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h2 className="text-base font-semibold">{t('settings.booking.statusSection')}</h2>
                {widget.isEnabled ? (
                  <Badge tone="success">{t('settings.booking.published')}</Badge>
                ) : (
                  <Badge tone="warning">{t('settings.booking.unpublished')}</Badge>
                )}
              </div>
              <label className="flex items-start gap-2 text-sm">
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={form.isEnabled}
                  disabled={!canConfigure}
                  onChange={(e) => setForm((p) => ({ ...p, isEnabled: e.target.checked }))}
                />
                <span>
                  <span className="font-medium">{t('settings.booking.publishToggle')}</span>
                  <span className="block text-xs text-ink-muted">{t('settings.booking.publishToggleHint')}</span>
                </span>
              </label>
            </section>

            <section className="space-y-3 rounded-xl border p-5">
              <h2 className="text-base font-semibold">{t('settings.booking.embedSection')}</h2>
              <p className="text-sm text-ink-muted">{t('settings.booking.embedSectionHint')}</p>
              <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50/60 p-3 text-sm dark:border-amber-900 dark:bg-amber-950/30">
                <Icon as={TriangleAlert} className="mt-0.5 shrink-0 text-amber-600 dark:text-amber-400" />
                <p>{t('settings.booking.embedIsCredential')}</p>
              </div>
              <div className="rounded-lg border bg-subtle p-3">
                {revealToken ? (
                  <code dir="ltr" className="block break-all font-mono text-sm">
                    {widget.embedToken}
                  </code>
                ) : (
                  <code dir="ltr" className="block font-mono text-sm text-ink-muted">
                    ••••••••••••••••••••••••••••••••
                  </code>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                {!revealToken ? (
                  <Button variant="outline" size="sm" onClick={() => setRevealToken(true)}>
                    {t('settings.booking.revealToken')}
                  </Button>
                ) : (
                  <Button variant="outline" size="sm" onClick={() => void copyToken(widget.embedToken)}>
                    <Icon as={Copy} />
                    {t('integrations.secret.copy')}
                  </Button>
                )}
                {canRotate && (
                  <Button variant="outline" size="sm" onClick={() => setRotating(true)}>
                    <Icon as={KeyRound} />
                    {t('settings.booking.rotateKey')}
                  </Button>
                )}
              </div>
            </section>

            <section className="space-y-4 rounded-xl border p-5">
              <h2 className="text-base font-semibold">{t('settings.booking.rulesSection')}</h2>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label={t('settings.booking.slotMinutes')} hint={t('settings.booking.slotMinutesHint')}>
                  <Input
                    type="number"
                    min={15}
                    max={480}
                    dir="ltr"
                    disabled={!canConfigure}
                    value={form.slotMinutes}
                    onChange={(e) => setForm((p) => ({ ...p, slotMinutes: e.target.value }))}
                  />
                </Field>
                <Field label={t('settings.booking.leadTimeMinutes')} hint={t('settings.booking.leadTimeMinutesHint')}>
                  <Input
                    type="number"
                    min={0}
                    max={10080}
                    dir="ltr"
                    disabled={!canConfigure}
                    value={form.leadTimeMinutes}
                    onChange={(e) => setForm((p) => ({ ...p, leadTimeMinutes: e.target.value }))}
                  />
                </Field>
                <Field label={t('settings.booking.horizonDays')} hint={t('settings.booking.horizonDaysHint')}>
                  <Input
                    type="number"
                    min={1}
                    max={365}
                    dir="ltr"
                    disabled={!canConfigure}
                    value={form.horizonDays}
                    onChange={(e) => setForm((p) => ({ ...p, horizonDays: e.target.value }))}
                  />
                </Field>
                <div />
                <Field
                  label={t('settings.booking.minPartySize')}
                  error={partyRangeInvalid ? t('settings.booking.partyRangeInvalid') : undefined}
                >
                  <Input
                    type="number"
                    min={1}
                    max={200}
                    dir="ltr"
                    disabled={!canConfigure}
                    value={form.minPartySize}
                    onChange={(e) => setForm((p) => ({ ...p, minPartySize: e.target.value }))}
                  />
                </Field>
                <Field label={t('settings.booking.maxPartySize')}>
                  <Input
                    type="number"
                    min={1}
                    max={200}
                    dir="ltr"
                    disabled={!canConfigure}
                    value={form.maxPartySize}
                    onChange={(e) => setForm((p) => ({ ...p, maxPartySize: e.target.value }))}
                  />
                </Field>
              </div>
              <label className="flex items-start gap-2 text-sm">
                <input
                  type="checkbox"
                  className="mt-0.5"
                  disabled={!canConfigure}
                  checked={form.autoConfirm}
                  onChange={(e) => setForm((p) => ({ ...p, autoConfirm: e.target.checked }))}
                />
                <span>
                  <span className="font-medium">{t('settings.booking.autoConfirm')}</span>
                  <span className="block text-xs text-ink-muted">{t('settings.booking.autoConfirmHint')}</span>
                </span>
              </label>
              <label className="flex items-start gap-2 text-sm">
                <input
                  type="checkbox"
                  className="mt-0.5"
                  disabled={!canConfigure}
                  checked={form.waitlistEnabled}
                  onChange={(e) => setForm((p) => ({ ...p, waitlistEnabled: e.target.checked }))}
                />
                <span>
                  <span className="font-medium">{t('settings.booking.waitlistEnabled')}</span>
                  <span className="block text-xs text-ink-muted">{t('settings.booking.waitlistEnabledHint')}</span>
                </span>
              </label>
            </section>

            <section className="space-y-3 rounded-xl border p-5">
              <h2 className="text-base font-semibold">{t('settings.booking.hoursSection')}</h2>
              <p className="text-sm text-ink-muted">{t('settings.booking.hoursSectionHint')}</p>
              <div className="space-y-2">
                {DAYS.map((day) => {
                  const w = windowOf(form.openingHours, day);
                  const open = w !== null;
                  return (
                    <div key={day} className="flex flex-wrap items-center gap-3">
                      <label className="flex w-32 items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          disabled={!canConfigure}
                          checked={open}
                          onChange={(e) => setWindow(day, e.target.checked ? {} : null)}
                        />
                        {t(`settings.booking.day.${day}`)}
                      </label>
                      {open && (
                        <div className="flex items-center gap-2">
                          <Input
                            type="time"
                            dir="ltr"
                            className="w-32"
                            disabled={!canConfigure}
                            value={w.from}
                            onChange={(e) => setWindow(day, { from: e.target.value })}
                          />
                          <span className="text-ink-muted">{t('settings.booking.hoursTo')}</span>
                          <Input
                            type="time"
                            dir="ltr"
                            className="w-32"
                            disabled={!canConfigure}
                            value={w.to}
                            onChange={(e) => setWindow(day, { to: e.target.value })}
                          />
                        </div>
                      )}
                      {!open && <span className="text-xs text-ink-muted">{t('settings.booking.dayClosed')}</span>}
                    </div>
                  );
                })}
              </div>
              {form.openingHours.length === 0 && (
                <p className="text-xs text-amber-700 dark:text-amber-400">{t('settings.booking.noHoursWarning')}</p>
              )}
            </section>

            {saveError && (
              <p role="alert" className="text-sm text-red-600 dark:text-red-400">
                {saveError}
              </p>
            )}
            {canConfigure && (
              <div className="flex justify-end">
                <Button onClick={() => void save()} disabled={saving || partyRangeInvalid}>
                  {saving ? t('settings.common.saving') : t('common.save')}
                </Button>
              </div>
            )}
          </>
        )
      )}

      <ConfirmDialog
        open={rotating}
        title={t('settings.booking.rotateKeyTitle')}
        message={t('settings.booking.rotateKeyConfirm', { name: branchName })}
        confirmLabel={t('settings.booking.rotateKey')}
        onConfirm={rotate}
        onClose={() => setRotating(false)}
      />
    </div>
  );
}
